import { ChevronDown } from 'lucide-react';
import { useState, useEffect } from "react";
import { useParams, Link, useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import LocalLink from "@/components/ui/LocalLink";
import { AnimatedTitle } from "@/components/ui/AnimatedText";
import ParallaxImage from "@/components/ui/ParallaxImage";
import LuxuryCard from "@/components/ui/LuxuryCard";
import { getPostBySlug, getPopularPosts, getAllCategories, recordPostView } from "@/utils/blogData";
import { useSEO } from "@/utils/useSEO";
import BookingWidget from "@/components/ui/BookingWidget";
import { mockRooms } from "@/utils/roomsData";

// Helper to map blog accommodation slug to actual villa data
const getRoomBySlug = (slug: string) => {
  return mockRooms.find(r => r.slug === slug) || mockRooms[0];
};


const getFirstParagraphExcerpt = (htmlContent: string): string => {
  const pMatch = htmlContent.match(/<p>([\s\S]*?)<\/p>/i);
  let text = pMatch ? pMatch[1] : htmlContent;
  text = text.replace(/<[^>]*>/g, ""); // Strip any HTML tags
  const words = text.trim().split(/\s+/);
  const excerptWords = words.slice(0, 164);
  return excerptWords.join(" ") + (words.length > 164 ? "..." : "");
};

export default function NewsDetail() {
  const { t } = useTranslation();
  const { slug } = useParams<{ slug: string }>();
  const navigate = useNavigate();
  const [searchQuery, setSearchQuery] = useState("");
  const [showBookingModal, setShowBookingModal] = useState(false);
  
  const post = slug ? getPostBySlug(slug) : undefined;
  const articleExcerpt = post ? getFirstParagraphExcerpt(post.content) : "";
  
  useSEO(
    post ? post.title : "News Article",
    post ? articleExcerpt : undefined,
    post ? post.image : undefined,
    {
      type: "article",
      publishedAt: post?.date ? new Date(post.date).toISOString() : undefined,
      author: post?.author?.name,
    }
  );

  useEffect(() => {
    if (post) {
      recordPostView(post.slug);
    }
  }, [post]);

  if (!post) {
    return (
      <div className="min-h-[80vh] flex flex-col items-center justify-center text-center p-6 bg-[#0B0B0B]">
        <h2 className="text-3xl font-bold text-white mb-4">{t("newsDetail.notFound", "Article Not Found")}</h2>
        <p className="text-white/60 mb-8">{t("newsDetail.notFoundDesc", "The news article you are looking for does not exist or has been moved.")}</p>
        <LocalLink to="/news" className="btn-gold px-8 py-3 rounded-full">
          {t("newsDetail.backToNews", "Back to News")}
        </LocalLink>
      </div>
    );
  }

  const popularPosts = getPopularPosts().filter(p => p.id !== post.id).slice(0, 4);
  const categories = getAllCategories();

  const handleSearchSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (searchQuery.trim()) {
      navigate(`/news/search?q=${encodeURIComponent(searchQuery)}`);
    }
  };

  return (
    <>
      {/* Hero Banner Section */}
      <section className="relative min-h-[100dvh] w-full flex items-center justify-center">
        <div className="absolute inset-0">
          <ParallaxImage
            src={post.image}
            alt={post.title}
            className="w-full h-full"
          />
          <div className="absolute inset-0 bg-gradient-to-t from-[#0B0B0B] via-[#0B0B0B]/30 to-black/40 z-10" />
        </div>
        
        <div className="relative z-20 container mx-auto px-6 text-left text-white max-w-6xl">
          <LocalLink
            to={`/news/category/${post.categorySlug}`}
            className="text-gold uppercase tracking-[0.2em] text-xs font-semibold px-3 py-1 bg-gold/10 border border-gold/20 rounded-full hover:bg-gold/20 transition-colors inline-block mb-6"
          >
            {t(`blogCategories.${post.categorySlug}.name`, { defaultValue: post.category })}
          </LocalLink>
          <AnimatedTitle
            title={t(`blog.${post.slug}.title`, { defaultValue: post.title })}
            className="text-4xl md:text-5xl lg:text-6xl font-luxury text-shadow-lg leading-tight lg:text-6xl font-bold mb-6 max-w-4xl text-white font-serif leading-tight"
          />
          <div className="flex flex-wrap items-center gap-6 text-sm text-white/60">
            <div className="flex items-center gap-2">
              <img
                src={post.author.avatar}
                alt={post.author.name}
                className="w-8 h-8 rounded-full border border-gold/30 object-cover"
              />
              <span className="text-white">{t(`blog.authors.${post.author.name}.name`, { defaultValue: post.author.name })}</span>
            </div>
            <span>|</span>
            <span>{t(`blog.${post.slug}.date`, { defaultValue: post.date })}</span>
          </div>
        </div>

        {/* Scroll Down Button */}
        <div className="absolute bottom-10 left-1/2 -translate-x-1/2 z-20">
          <button
            onClick={() => window.scrollTo({ top: window.innerHeight, behavior: 'smooth' })}
            className="text-white/70 hover:text-white transition-colors duration-300 flex flex-col items-center gap-2 cursor-pointer"
            aria-label="Scroll down"
          >
            <span className="uppercase tracking-[0.2em] text-[10px]">{t("newsDetail.readArticle", "Read Article")}</span>
            <ChevronDown size={24} className="animate-bounce" />
          </button>
        </div>
      </section>

      {/* Main Grid Content */}
      <section className="py-20 bg-[#0B0B0B]">
        <div className="container mx-auto px-6 max-w-6xl">
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-12">
            
            {/* Left Column: Post Content & Author */}
            <article className="lg:col-span-8 flex flex-col gap-10">
              
              {/* HTML Content rendering with custom styling */}
              <div 
                className="text-white/80 leading-relaxed text-base md:text-lg font-light space-y-6 
                           prose prose-invert prose-gold max-w-none 
                           [&_h2]:text-3xl [&_h2]:font-serif [&_h2]:text-gold [&_h2]:mt-10 [&_h2]:mb-4
                           [&_h3]:text-2xl [&_h3]:font-serif [&_h3]:text-emerald-400 [&_h3]:mt-8 [&_h3]:mb-4
                           [&_a]:font-bold [&_a]:text-white/90 hover:[&_a]:text-gold [&_a]:transition-colors [&_a]:underline
                           [&_p_em]:text-gold/90 [&_p_strong]:text-white [&_p_strong]:font-semibold"
                dangerouslySetInnerHTML={{ __html: t(`blog.${post.slug}.content`, { defaultValue: post.content }) }}
              />

              {/* Tags Section */}
              <div className="border-t border-white/10 pt-6">
                <h4 className="text-xs uppercase tracking-widest text-gold mb-3 font-semibold">{t("newsDetail.tags", "Tags")}</h4>
                <div className="flex flex-wrap gap-2">
                  {post.tags.map((tag, i) => (
                    <span 
                      key={i}
                      className="text-xs px-3 py-1 bg-white/5 border border-white/10 rounded-full text-white/70"
                    >
                      #{t(`blog.tags.${tag}`, { defaultValue: tag })}
                    </span>
                  ))}
                </div>
              </div>

              {/* Author Profile Card */}
              <div className="bg-[#121212] border border-white/5 rounded-2xl p-6 md:p-8 flex flex-col md:flex-row gap-6 items-center md:items-start mt-6">
                <img
                  src={post.author.avatar}
                  alt={post.author.name}
                  className="w-20 h-20 rounded-full border-2 border-gold object-cover shrink-0"
                />
                <div className="text-center md:text-left">
                  <span className="text-xs uppercase tracking-widest text-gold font-semibold block mb-1">
                    {t(`blog.authors.${post.author.name}.role`, { defaultValue: post.author.role })}
                  </span>
                  <h4 className="text-xl font-bold text-white mb-3">{t(`blog.authors.${post.author.name}.name`, { defaultValue: post.author.name })}</h4>
                  <p className="text-white/60 text-sm leading-relaxed font-light">{t(`blog.authors.${post.author.name}.bio`, { defaultValue: post.author.bio })}</p>
                </div>
              </div>

            </article>

            {/* Right Column: Widgets */}
            <aside className="lg:col-span-4 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-1 gap-10 lg:sticky lg:top-28 lg:self-start">
              
              {/* Search Widget */}
              <div className="bg-[#121212] border border-white/5 rounded-2xl p-6">
                <h3 className="text-lg font-serif text-white mb-4 border-b border-gold/20 pb-2">{t("newsDetail.search", "Search")}</h3>
                <form onSubmit={handleSearchSubmit} className="flex">
                  <input
                    type="text"
                    placeholder={t("newsDetail.searchPlaceholder", "Search articles...")}
                    value={searchQuery}
                    onChange={(e) => setSearchQuery(e.target.value)}
                    className="w-full bg-black text-white border border-white/10 px-4 py-2.5 rounded-l-lg focus:outline-none focus:border-gold transition-colors text-sm"
                  />
                  <button
                    type="submit"
                    className="bg-gold text-black px-4 rounded-r-lg font-semibold hover:bg-gold/90 transition-colors text-sm"
                  >
                    {t("newsDetail.go", "Go")}
                  </button>
                </form>
              </div>

              {/* Category Widget */}
              <div className="bg-[#121212] border border-white/5 rounded-2xl p-6">
                <h3 className="text-lg font-serif text-white mb-4 border-b border-gold/20 pb-2">{t("newsDetail.categories", "Categories")}</h3>
                <ul className="flex flex-col gap-3">
                  {categories.map((cat, i) => (
                    <li key={i}>
                      <LocalLink 
                        to={`/news/category/${cat.slug}`}
                        className="flex justify-between items-center text-white/70 hover:text-gold transition-colors text-sm"
                      >
                        <span>{t(`blogCategories.${cat.slug}.name`, { defaultValue: cat.name })}</span>
                        <span className="bg-white/5 border border-white/10 px-2 py-0.5 rounded text-xs text-white/50">
                          {cat.count}
                        </span>
                      </LocalLink>
                    </li>
                  ))}
                </ul>
              </div>

              {/* Popular Posts Widget */}
              {popularPosts.length > 0 && (
                <div className="bg-[#121212] border border-white/5 rounded-2xl p-6">
                  <h3 className="text-lg font-serif text-white mb-4 border-b border-gold/20 pb-2">{t("newsDetail.popularPosts", "Popular Posts")}</h3>
                  <div className="flex flex-col gap-4">
                    {popularPosts.map((popPost) => (
                      <LocalLink 
                        key={popPost.id} 
                        to={`/news/${popPost.slug}`}
                        className="flex gap-4 group"
                      >
                        <div className="w-16 h-16 rounded overflow-hidden shrink-0 bg-neutral-900">
                          <img
                            src={popPost.image}
                            alt={popPost.title}
                            className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-500"
                          />
                        </div>
                        <div className="flex flex-col justify-center">
                          <h4 className="text-sm font-semibold text-white group-hover:text-gold transition-colors line-clamp-2 leading-snug">
                            {t(`blog.${popPost.slug}.title`, { defaultValue: popPost.title })}
                          </h4>
                          <span className="text-xs text-white/40 mt-1">{t(`blog.${popPost.slug}.date`, { defaultValue: popPost.date })}</span>
                        </div>
                      </LocalLink>
                    ))}
                  </div>
                </div>
              )}

              {/* Promotion / Booking Banner */}
              <div className="relative rounded-2xl overflow-hidden h-80 flex flex-col justify-end p-6 group">
                <div className="absolute inset-0 z-0">
                  <img
                    src="/media/patal-kikian-hero-banner.webp"
                    alt="Promo Banner"
                    className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-700"
                  />
                  <div className="absolute inset-0 bg-gradient-to-t from-black via-black/40 to-transparent z-10" />
                </div>
                <div className="relative z-20 text-center">
                  <span className="text-xs uppercase tracking-widest text-gold font-semibold block mb-2">
                    {t("newsDetail.exclusiveStay", "Exclusive Stay")}
                  </span>
                  <h3 className="text-xl font-serif text-white mb-4">{t("newsDetail.promoDesc", "Experience Premium Luxury in Sidemen")}</h3>
                  <button
                    onClick={() => setShowBookingModal(true)}
                    className="w-full py-3 bg-gold text-black rounded-full font-semibold inline-block text-sm hover:bg-gold/90 transition-colors uppercase tracking-wider cursor-pointer border-0"
                  >
                    {t("newsDetail.bookNow", "Book Now")}
                  </button>
                </div>
              </div>

            </aside>

          </div>
        </div>
      </section>

      {/* Recommended Accommodations Section */}
      {post.accommodationSlugs && post.accommodationSlugs.length > 0 && (
        <section className="py-20 border-t border-white/5 bg-[#090909]">
          <div className="container mx-auto px-6 max-w-6xl">
            <div className="text-center mb-12">
              <span className="text-gold uppercase tracking-[0.2em] text-xs font-semibold block mb-3">
                {t("newsDetail.villaRecs", "Villa Recommendations")}
              </span>
              <h2 className="text-3xl md:text-4xl font-serif font-bold text-white">{t("newsDetail.recAccommodations", "Recommended Accommodations")}</h2>
            </div>
            
            <div className="grid grid-cols-1 md:grid-cols-2 gap-8 max-w-4xl mx-auto">
              {post.accommodationSlugs.map((roomSlug, i) => {
                const room = getRoomBySlug(roomSlug);
                return (
                  <LuxuryCard
                    key={i}
                    title={room.title}
                    image={room.image}
                    href={`/accommodation/${room.slug}`}
                    description={room.description}
                    features={room.features}
                    price={room.price}
                    delay={i * 0.1}
                  />
                );
              })}
            </div>
          </div>
        </section>
      )}

      {/* Booking Modal Popup */}
      {showBookingModal && (
        <div className="fixed inset-0 z-[9999] flex items-center justify-center bg-black/85 backdrop-blur-md p-4 animate-fade-in">
          <div className="relative w-full max-w-5xl bg-[#121212] border border-white/10 rounded-2xl p-5 sm:p-8 shadow-2xl animate-scale-up max-h-[95vh] overflow-y-auto">
            {/* Close Button */}
            <button 
              onClick={() => setShowBookingModal(false)}
              className="absolute top-4 right-4 text-white/50 hover:text-white transition-colors cursor-pointer p-2 z-55 bg-transparent border-0"
              aria-label="Close modal"
            >
              <svg xmlns="http://www.w3.org/2000/svg" className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                <path strokeLinecap="round" strokeLinejoin="round" d="M6 18L18 6M6 6l12 12" />
              </svg>
            </button>

            <div className="text-center mb-4 mt-2">
              <span className="text-gold uppercase tracking-[0.2em] text-xs font-semibold block mb-1">{t("newsDetail.bookStay", "Book Your Stay")}</span>
              <h3 className="text-2xl sm:text-3xl font-luxury text-white">{t("newsDetail.reserveStay", "Reserve Your Stay")}</h3>
            </div>

            {/* Embed Booking Widget */}
            <div className="py-2">
              <BookingWidget />
            </div>
          </div>
        </div>
      )}
    </>
  );
}
